/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useEffect, useMemo, useState} from 'react'
import {MAIN_IMAGES_IMAGE_TYPE} from '../constants'
import useOcapiSite from '../commerce-api/hooks/useOcapiSite'
import {findImageGroupBy} from '../utils/image-groups-utils'
import {useVariant} from './use-variant'
import {useIntl} from 'react-intl'
import {useVariationParams} from './use-variation-params'
import {useVariationAttributes} from './use-variation-attributes'
import {isProductEngravable, isProductOrderable, isProductSet} from '../utils/product-utils'

const OUT_OF_STOCK = 'OUT_OF_STOCK'
const UNFULFILLABLE = 'UNFULFILLABLE'

// TODO: This needs to be refactored.
export const useProduct = (product, isProductPartOfSet = false) => {
    const showLoading = !product
    const stockLevel = product?.inventory?.stockLevel || 0
    const stepQuantity = product?.stepQuantity || 1
    const minOrderQuantity = stockLevel > 0 ? product?.minOrderQuantity || 1 : 0
    const initialQuantity = product?.quantity || product?.minOrderQuantity || 1

    const intl = useIntl()
    const {arEnabled, arModelList, brightcoveEnabled, show3Dimages, accountId, engravingImage} =
        useOcapiSite()
    const variant = useVariant(product, isProductPartOfSet)
    const variationParams = useVariationParams(product, isProductPartOfSet)
    const variationAttributes = useVariationAttributes(product, isProductPartOfSet)
    const [quantity, setQuantity] = useState(initialQuantity)

    // A product is considered out of stock if the stock level is 0 or if we have all our
    // variation attributes selected, but don't have a variant. We do this because the API
    // will sometimes return all the variants even if they are out of stock, but for other
    // products it won't.
    const isOutOfStock =
        !stockLevel ||
        (!variant && Object.keys(variationParams).length === variationAttributes.length)
    const unfulfillable = stockLevel < quantity
    const inventoryMessages = {
        [OUT_OF_STOCK]: intl.formatMessage({
            defaultMessage: 'Out of stock',
            id: 'use_product.message.out_of_stock'
        }),
        [UNFULFILLABLE]: intl.formatMessage(
            {
                defaultMessage: 'Only {stockLevel} left!',
                id: 'use_product.message.inventory_remaining'
            },
            {stockLevel}
        )
    }
    const showInventoryMessage = variant && (isOutOfStock || unfulfillable)
    const inventoryMessage =
        (isOutOfStock && inventoryMessages[OUT_OF_STOCK]) ||
        (unfulfillable && inventoryMessages[UNFULFILLABLE])

    // If the `initialQuantity` changes, update the state. This typically happens
    // when either the master product changes, or the inventory of the product changes
    // from out-of-stock to in-stock or vice versa.
    useEffect(() => {
        setQuantity(initialQuantity)
    }, [initialQuantity])

    // parse product hotspots if available
    const hotspots = useMemo(() => {
        const relatedHotspotPids =
            product?.variants?.map((variant) => {
                return variant.productId
            }) || []
        if (product?.master?.masterId) {
            relatedHotspotPids.push(product?.master?.masterId)
        }
        // remove hotspots related to master or any variant products
        const filteredHotspots = product?.c_hotspots || {}
        const mainImages = filteredHotspots?.[MAIN_IMAGES_IMAGE_TYPE] || {}
        Object.keys(mainImages).forEach((img) => {
            mainImages[img] = mainImages[img].reduce((result, hotspot) => {
                if (!relatedHotspotPids.includes(hotspot.pid)) {
                    result.push(hotspot)
                }
                return result
            }, [])
        })
        return filteredHotspots
    }, [product])

    const videos = useMemo(() => {
        if (brightcoveEnabled) {
            try {
                const videos = Object.values(JSON.parse(product?.c_BrightcoveVideoProduct))
                    .filter((video) => Object.keys(video).includes('videoId', 'playerId'))
                    .map((video) => {
                        return {
                            accountId,
                            ...video
                        }
                    })

                return videos
            } catch (e) {
                console.error('Error parsing product videos', e)
                return false
            }
        }
    }, [brightcoveEnabled, product?.c_BrightcoveVideoProduct, accountId])

    const ARModel = useMemo(() => {
        if (!arEnabled || arModelList == null) {
            return null
        }

        // The arModelList is retreived from the backend and it only
        // contains ids of product variants, not of master products.
        // If the current product is of type "master"
        // then we get the id of its first variant.
        let variantId
        if (product?.type?.variant) {
            variantId = product?.id
        } else if (product?.variants?.length) {
            variantId = product.variants[0]?.productId
        }

        return variantId in arModelList ? arModelList[variantId] : null
    }, [product, arModelList, arEnabled])

    const isSet = isProductSet(product)

    // The variant id of the product that has 3D images
    const variantIdWith3D = useMemo(() => {
        // Prevent unnecessary computations if 3D images are not enabled
        if (!show3Dimages) {
            return null
        }

        // If the product has 3D images, return the product id
        if (
            findImageGroupBy(product?.imageGroups, {
                viewType: '3Dimages',
                selectedVariationAttributes: variationParams
            })?.images?.length > 0
        ) {
            return product?.type?.variant ? product?.id : product?.variants?.[0]?.productId
        }

        if (isSet) {
            // If the product is a set, iterate over its set products
            // and return first product id that has 3D images
            for (const item of product?.setProducts) {
                if (
                    findImageGroupBy(item?.imageGroups, {
                        viewType: '3Dimages',
                        selectedVariationAttributes: variationParams
                    })?.images?.length > 0
                ) {
                    return item?.type?.variant ? item?.id : item?.variants?.[0]?.productId
                }
            }
        }

        return null
    }, [product, show3Dimages, variationParams, isSet])

    const has3D = !!variantIdWith3D && show3Dimages

    const configurator3DLink = product?.c_absConfiguratorLink
    const redirectLinkPartner = product?.c_redirectLinkPartner

    const isEngravable = useMemo(() => isProductEngravable(product), [product])

    const masterSubtypeTooltipText = product?.c_masterSubtypeTooltipText

    const isOrderable = useMemo(() => isProductOrderable(product), [product])

    const setIncludesBonusProduct = isSet && product?.c_bonus_product_included

    return {
        has3D,
        ARModel,
        videos,
        showLoading,
        showInventoryMessage,
        inventoryMessage,
        variationAttributes,
        quantity,
        minOrderQuantity,
        stepQuantity,
        variationParams,
        setQuantity,
        variant,
        stockLevel,
        hotspots,
        configurator3DLink,
        redirectLinkPartner,
        engravingImage,
        isEngravable,
        masterSubtypeTooltipText,
        isSet,
        isOrderable,
        setIncludesBonusProduct,
        variantIdWith3D
    }
}
